/* eslint-disable @nx/enforce-module-boundaries */
import {
  Component,
  Inject,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import {
  LoaderIndicatorDirective,
  LoaderService,
  NotificationService,
} from '@t-llevo/common-ui';
import { DialogHeaderComponent } from 'admin-drivers/src/app/common/dialog/components/dialog-header/dialog-header.component';
import {
  AbstractControl,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { FormDialogData } from 'admin-drivers/src/app/common/models/FormDialogData';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { TravelPricingService } from 'admin-drivers/src/app/api/services/travel-pricing.service';
import { Currency } from '../../const/currency';
import { VehicleTypesService } from 'admin-drivers/src/app/api/services/vehicle-types.service';
import { AsyncPipe } from '@angular/common';
import { UploadImageService } from 'admin-drivers/src/app/api/services/upload-image.service';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTimepickerModule } from '@angular/material/timepicker';
import { provideNativeDateAdapter } from '@angular/material/core';
import weekDays from '../../const/week-days';
import { MatExpansionModule } from '@angular/material/expansion';
import { BehaviorSubject, map } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { TravelPricingRequest } from 'admin-drivers/src/app/api/models/requests/TravelPricingRequest';
import { TravelPricing } from 'admin-drivers/src/app/common/models/TravelPrincing';

@Component({
  selector: 't-llevo-travel-pricing-form',
  standalone: true,
  imports: [
    MatDialogModule,
    MatButtonModule,
    LoaderIndicatorDirective,
    DialogHeaderComponent,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatCheckboxModule,
    AsyncPipe,
    MatStepperModule,
    MatTimepickerModule,
    MatExpansionModule,
  ],
  styles: `
    @import '../../../utils/styles/baseDialog.scss';
    @import '../../../utils/styles/formGroup.scss';

    t-llevo-image-input {
      display: flex;
      width: 100%;
      min-height: 200px;
      max-height: 350px;
    }
  `,
  providers: [
    LoaderService,
    TravelPricingService,
    VehicleTypesService,
    UploadImageService,
    provideNativeDateAdapter(),
  ],
  templateUrl: './travel-pricing-form.component.html',
})
export class TravelPricingFormComponent implements OnInit {
  private _pricingSrv = inject(TravelPricingService);
  protected _vehicleTypesSrv = inject(VehicleTypesService);
  private _notifSrv = inject(NotificationService);
  private _loaderSrv = inject(LoaderService);

  protected loading = toSignal(this._loaderSrv.loaderState$);
  protected generalFrm = this._fb.group({
    vehicleType: ['', Validators.required],
    currency: ['', Validators.required],
    baseFee: [1, [Validators.required, Validators.min(1)]],
    costPerKm: [1, [Validators.required, Validators.min(1)]],
  });

  protected ruleFrm = this._fb.group(
    {
      weekDay: this._fb.control<string | null>(null),
      validTimeRangeStart: this._fb.control<Date | null>(null),
      validTimeRangeEnd: this._fb.control<Date | null>(null),
      geofence: [''],
    },
    { validators: this._requireBothIfOneIsFilledValidator }
  );

  protected extraFrm = this._fb.group({
    costPerMinute: [0],
    pickupFee: [0],
    dropoffFee: [0],
    surgeMultiplier: [1, [Validators.min(1)]],
    minimumFare: [0],
    cancellationFee: [0],
    distanceThresholdKm: [0],
  });

  protected availableCurrency = Currency;
  protected weekDaysList = weekDays;

  availableVehicleTypes$ = this._vehicleTypesSrv.getVehicleTypes().pipe(
    map((data) => {
      return data.filter((vehicle) => vehicle.available);
    })
  );

  private stepsSubject = new BehaviorSubject(0);
  step$ = this.stepsSubject.asObservable();

  protected _edit = signal(false);
  protected dialogTitle = computed(
    () => `${this._edit() ? 'Editar' : 'Adicionar'} precio`
  );
  protected submitBtn = computed(() => (this._edit() ? 'Editar' : 'Adicionar'));

  constructor(
    private _dialogRef: MatDialogRef<TravelPricingFormComponent>,
    @Inject(MAT_DIALOG_DATA)
    public formData: FormDialogData<TravelPricing> | undefined,
    private _fb: NonNullableFormBuilder
  ) {}

  ngOnInit(): void {
    this._vehicleTypesSrv.getVehicleTypes().subscribe();
    if (this.formData && this.formData.edit) {
      this._loaderSrv.show();
      this._edit.set(true);
      this.assignData();
      this._loaderSrv.hide();
    }
  }

  private assignData() {
    if (this.formData) {
      this.vehicleType.setValue(this.formData.data.typeVehicle);
      this.currency.setValue(this.formData.data.currency);
      this.baseFee.setValue(this.formData.data.baseFee);
      this.costPerKm.setValue(this.formData.data.costPerKm);

      this.weekDay.setValue(
        this.formData.data.rule?.weekDay?.toString() ?? null
      );
      this.validTimeRangeStart.setValue(
        this.formData.data.rule?.validTimeRange?.start ?? null
      );
      this.validTimeRangeEnd.setValue(
        this.formData.data.rule?.validTimeRange?.end ?? null
      );
      this.geofence.setValue(this.formData.data.rule?.geofence ?? '');
      this.costPerMinute.setValue(this.formData.data.costPerMinute);
      this.pickupFee.setValue(this.formData.data.pickupFee);
      this.dropoffFee.setValue(this.formData.data.dropoffFee);
      this.surgeMultiplier.setValue(this.formData.data.surgeMultiplier);
      this.minimumFare.setValue(this.formData.data.minimumFare);
      this.cancellationFee.setValue(this.formData.data.cancellationFee);
      this.distanceThresholdKm.setValue(this.formData.data.distanceThresholdKm);
    }
  }

  onSubmit() {
    this.generalFrm.markAllAsTouched();
    this.ruleFrm.markAllAsTouched();
    this.extraFrm.markAllAsTouched();

    if (this.generalFrm.valid && this.ruleFrm.valid && this.extraFrm.valid) {
      this._loaderSrv.show();
      const request = this._edit()
        ? this._pricingSrv.updatePrice(
            this._getFormatedFormData(),
            this.formData?.data.id ?? ''
          )
        : this._pricingSrv.createPrice(this._getFormatedFormData());

      request.subscribe({
        next: () => {
          const notificationMsg = `Precio ${
            this._edit() ? 'editado' : 'creado'
          }`;
          this._loaderSrv.hide();
          this._notifSrv.dispatch(notificationMsg, 'success');
          this._dialogRef.close(true);
        },
        error: (err: HttpErrorResponse) => {
          this._loaderSrv.hide();
          this._notifSrv.dispatch(err.error.message, 'error');
        },
      });
    }
  }

  private _getFormatedFormData(): TravelPricingRequest {
    const general = this.generalFrm.getRawValue();
    const rule = this.ruleFrm.getRawValue();
    const extra = this.extraFrm.getRawValue();
    return {
      currency: general.currency,
      base_fee: general.baseFee,
      cost_per_km: general.costPerKm,
      cost_per_minute: extra.costPerMinute,
      pickup_fee: extra.pickupFee,
      dropoff_fee: extra.dropoffFee,
      surge_multiplier: extra.surgeMultiplier,
      minimum_fare: extra.minimumFare,
      cancellation_fee: extra.cancellationFee,
      distance_threshold_km: extra.distanceThresholdKm,
      typeVehicle: general.vehicleType,
      rule: {
        week_day: rule.weekDay !== null ? Number(rule.weekDay) : null,
        valid_time_range:
          rule.validTimeRangeStart && rule.validTimeRangeEnd
            ? {
                start: rule.validTimeRangeStart,
                end: rule.validTimeRangeEnd,
              }
            : null,
        geofence: rule.geofence || null,
      },
      is_active: true,
    };
  }

  private _requireBothIfOneIsFilledValidator(control: AbstractControl): null {
    const startControl = control.get('validTimeRangeStart');
    const endControl = control.get('validTimeRangeEnd');

    const start = startControl?.value;
    const end = endControl?.value;

    if (start && !end) {
      endControl?.setErrors({ requireBothIfOneIsFilled: true });
    } else if (!start && end) {
      startControl?.setErrors({ requireBothIfOneIsFilled: true });
    } else {
      endControl?.setErrors(null);
      startControl?.setErrors(null);
    }
    return null;
  }

  get vehicleType() {
    return this.generalFrm.controls['vehicleType'];
  }

  get currency() {
    return this.generalFrm.controls['currency'];
  }

  get baseFee() {
    return this.generalFrm.controls['baseFee'];
  }

  get costPerKm() {
    return this.generalFrm.controls['costPerKm'];
  }

  get weekDay() {
    return this.ruleFrm.controls['weekDay'];
  }

  get validTimeRangeStart() {
    return this.ruleFrm.controls['validTimeRangeStart'];
  }

  get validTimeRangeEnd() {
    return this.ruleFrm.controls['validTimeRangeEnd'];
  }

  get geofence() {
    return this.ruleFrm.controls['geofence'];
  }

  get costPerMinute() {
    return this.extraFrm.controls['costPerMinute'];
  }

  get pickupFee() {
    return this.extraFrm.controls['pickupFee'];
  }

  get dropoffFee() {
    return this.extraFrm.controls['dropoffFee'];
  }

  get surgeMultiplier() {
    return this.extraFrm.controls['surgeMultiplier'];
  }

  get minimumFare() {
    return this.extraFrm.controls['minimumFare'];
  }

  get cancellationFee() {
    return this.extraFrm.controls['cancellationFee'];
  }

  get distanceThresholdKm() {
    return this.extraFrm.controls['distanceThresholdKm'];
  }
}
